/**
 * The stop sheets, one per stop in route order after the pull & pack list (the
 * Oct 21 canvas; Abner, Oct 18: "Simplified version that consolidates pack
 * list and stop sheet into one"): what goes to the school in one table per
 * meal with its containers and the copy's temperature columns, then, on the
 * kitchen copy, one sign-off box. Milk is its own table on top of the meals
 * (PROD-1502; Abner, Oct 19: "MILK should be displayed SEPARATELY as its own
 * table (everywhere!)"). A later day's food sits under its own band.
 *
 * The head (the Oct 21 canvas): the date stamp, the stop's number in a black
 * badge of the same height, "STOP 1 OF 5 · KITCHEN COPY" over the site and the
 * line under it; then one outlined box, the delivery window styled like the
 * date beside the copy's instructions.
 *
 * Two copies (Matt, Oct 20), the kitchen's on top: the KITCHEN COPY, when the
 * print dialog asks for it, names the caterer in its header, prints each
 * row's DELIVER temp (CHILLED for food delivered cold), takes the Kitchen °F
 * and Drop-off °F and the Received tick, and ends with its Sign-Off box
 * (packed by, then receiving); the SCHOOL COPY carries a black SCHOOL COPY
 * tag, prints each row's SERVE temp, takes the school's Received °F, Time and
 * OK, and ends with its own Sign-Off box, receiving only (the Oct 21
 * mockup signs both copies; Sheila, Oct 23: titled "Sign-Off"). Keep the
 * kitchen copy's "Kitchen °F" and "Drop-off °F" labels verbatim:
 * `temp-log-vision.ts` reads them off a photo of the sheet.
 *
 * Pure; unit-tested.
 */

import { deliverableTypeGroup } from '@/lib/chefs/pack-groups'
import { campusSupplyRows, visibleSupplyLines } from '@/lib/chefs/service-materials'
import { sortDeliverables } from '@/lib/chefs/totals'
import type { Campus, Deliverable, MealtimeBlock } from '@/lib/chefs/types'
import { MEALTIME_LABEL } from '@/lib/chefs/types'

import { CHEF_SERVICE_MATERIALS_VISIBLE } from '../../../../../shared/chef-packaging-visibility'
import type { PacketDay } from './day'
import { campusMealSections, isEntree, type MealSection, mealtimeCaps, serveDayLong, serveDayShort } from './meals'
import { laterMealWords, routeInstructions } from './route'
import { milkStopRow, stopRow } from './rows'
import type { PacketStop } from './stops'
import { todayThenLater } from './summaries'
import { type PacketSection, type PacketUnit, repeatOf, type StopCopy, type StopRowData } from './units'

export type { StopCopy } from './units'

function isCondimentRow(d: Pick<Deliverable, 'productType' | 'components'>): boolean {
  return deliverableTypeGroup(d) === 'condiments'
}

/** Entrées first, then sides, then condiments; the Orders row order within (hot first, then by name). */
function byRole(deliverables: readonly Deliverable[]): Deliverable[] {
  const rank = (d: Deliverable) => (isEntree(d) ? 0 : isCondimentRow(d) ? 2 : 1)
  return sortDeliverables([...deliverables]).sort((a, b) => rank(a) - rank(b))
}

/** The menus' delivery and service notes for a meal (as the old summary sheet printed them), or null when it has none. */
function menuNotesUnit(id: string, group: string, block: MealtimeBlock): PacketUnit | null {
  const notes = (block.menuInstructions ?? []).filter((note) => note.delivery || note.service)
  if (notes.length === 0) {
    return null
  }
  return { id, group, role: 'row', kind: 'menu-notes', notes }
}

/**
 * One meal's table: its band and column labels, the menus' notes, then the
 * rows (entrées, sides, condiments). An order-group campus lists each group
 * under its own sub-band (PROD-1296). A block that feeds today and a later
 * day cannot be split by day (its rows merge across the days): its band says
 * so. A later day's food prints under a black band, "DO NOT SERVE TODAY".
 */
function mealUnits(sid: string, section: MealSection, copy: StopCopy): PacketUnit[] {
  const group = `${sid}|meal|${section.key}`
  const block = section.block
  const row = (key: string, data: StopRowData): PacketUnit => ({
    id: `${group}|row|${key}`,
    group,
    role: 'row',
    kind: 'stop-row',
    copy,
    ...data,
  })
  const subBand = (key: string, text: string, note: string | null): PacketUnit => ({
    id: `${group}|group|${key}`,
    group,
    role: 'band',
    kind: 'band',
    text,
    note,
    level: 2,
  })
  const text = `${mealtimeCaps(section.mealtime)} · ${section.serve.label}`
  const units: PacketUnit[] = [
    section.serve.later
      ? {
          id: `${group}|band`,
          group,
          role: 'band',
          kind: 'band',
          text,
          note: null,
          level: 1,
          tone: 'dark',
        }
      : {
          id: `${group}|band`,
          group,
          role: 'band',
          kind: 'band',
          text,
          note: null,
          level: 1,
        },
    { id: `${group}|labels`, group, role: 'band', kind: 'stop-labels', copy },
  ]
  const notes = menuNotesUnit(`${group}|notes`, group, block)
  if (notes) {
    units.push(notes)
  }

  if (block.groups && block.groups.length > 0) {
    for (const orderGroup of block.groups) {
      units.push(
        subBand(
          orderGroup.key,
          orderGroup.label.toUpperCase(),
          `${orderGroup.meals.toLocaleString()} ${orderGroup.meals === 1 ? 'meal' : 'meals'}`,
        ),
      )
      for (const d of byRole(orderGroup.deliverables)) {
        units.push(row(d.id, stopRow(d)))
      }
    }
    return units
  }

  for (const d of byRole(block.deliverables)) {
    units.push(row(d.id, stopRow(d)))
  }
  return units
}

/**
 * The milk band's words: "MILK", "MILK · 2-DAY SUPPLY" with each day's share
 * ("today 330 · Mon 325") when the milk is for exactly the days this sheet's
 * meals are, and a black "DO NOT SERVE TODAY" band when all of it is for a
 * later day (Monday's jugs must not be poured Thursday).
 */
function milkBand(
  campus: Campus,
  _sections: readonly MealSection[],
  deliveryIso: string,
): Pick<Extract<PacketUnit, { kind: 'band' }>, 'text' | 'note' | 'tone'> {
  const lines = campus.milk ?? []
  const days = [...new Set(lines.flatMap((line) => line.serviceDates))].sort()
  const later = days.length > 0 && days.every((day) => day > deliveryIso)
  if (later) {
    const from = serveDayShort(days[0]!)
    return {
      text: days.length > 1 ? `MILK · ${days.length}-DAY SUPPLY FROM ${from}` : `MILK · SERVE ${from}`,
      note: null,
      tone: 'dark',
    }
  }
  if (days.length <= 1) {
    return { text: 'MILK', note: null }
  }
  return {
    text: `MILK · ${days.length}-DAY SUPPLY`,
    note: null,
  }
}

/** The milk table: its band and column labels, then every milk row whole, in its containers, the most first. */
function milkUnits(
  sid: string,
  campus: Campus,
  sections: readonly MealSection[],
  deliveryIso: string,
  copy: StopCopy,
): PacketUnit[] {
  const lines = [...(campus.milk ?? [])].sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name))
  if (lines.length === 0) {
    return []
  }
  const group = `${sid}|milk`
  return [
    { id: `${group}|band`, group, role: 'band', kind: 'band', level: 1, ...milkBand(campus, sections, deliveryIso) },
    { id: `${group}|labels`, group, role: 'band', kind: 'stop-labels', copy },
    ...lines.map(
      (line, i): PacketUnit => ({
        id: `${group}|row|${i}`,
        group,
        role: 'row',
        kind: 'stop-row',
        copy,
        ...milkStopRow(line),
      }),
    ),
  ]
}

/** The copy's words: "KITCHEN COPY" in its eyebrow, the school's "SCHOOL COPY" tag in its header (the Oct 21 canvas). */
const COPY_WORDS: Record<StopCopy, string> = { school: 'SCHOOL COPY', kitchen: 'KITCHEN COPY' }

/** The school copy's receiving line (the Oct 21 canvas), and the day each later meal at the stop is for. */
function onArrival(stop: PacketStop, deliveryIso: string): string[] {
  const later = campusMealSections(stop.campus, deliveryIso).flatMap((section) =>
    section.serve.later && section.serve.laterDay
      ? [
          `${MEALTIME_LABEL[section.mealtime]} is for ${serveDayLong(section.serve.laterDay)} ${serveDayShort(section.serve.laterDay).split(' ')[1]}.`,
        ]
      : [],
  )
  return [
    'Check each item against this list, take the temperature of hot and chilled items, and note anything missing, short or out of temp.',
    ...later,
  ]
}

/** The kitchen copy's delivery notes: the school's own door notes, then any food for a later day. */
function deliveryNotes(stop: PacketStop, deliveryIso: string): string[] {
  return [
    ...routeInstructions(stop.route?.instructions ?? stop.campus.info.parking, stop.window !== null).map(
      (line) => line.text,
    ),
    ...laterMealWords(stop.campus, deliveryIso),
  ]
}

export function stopSummarySectionId(dayIso: string, number: string, copy: StopCopy): string {
  return `${dayIso}|stop|${number}|${copy}`
}

/** One copy of a stop's summary. */
export function stopSummarySection(stop: PacketStop, day: PacketDay, copy: StopCopy): PacketSection {
  const number = stop.identity.number
  const sid = stopSummarySectionId(day.iso, number, copy)
  const campus = stop.campus
  const sections = campusMealSections(campus, day.iso)
  const delivered = !campus.isDryRun || campus.dryRunServiceType === 'TASTING'

  const units: PacketUnit[] = [
    {
      id: `${sid}|head`,
      group: null,
      role: 'block',
      kind: 'stop-head',
      copy,
      stop: stop.identity,
      eyebrow: `STOP ${number} OF ${day.stops.length} · ${COPY_WORDS[copy]}`,
      dateBox: day.dateBox,
      window: stop.window,
      windowNote: stop.windowNote,
      address: stop.address,
      contactName: copy === 'kitchen' ? stop.contactName : null,
      contactPhone: copy === 'kitchen' ? stop.contactPhone : null,
      instructionsLabel: copy === 'kitchen' ? 'Delivery' : 'On arrival',
      instructions: copy === 'kitchen' ? deliveryNotes(stop, day.iso) : onArrival(stop, day.iso),
      warning: stop.warning,
      keepWithNext: true,
    },
  ]
  const milk = milkUnits(sid, campus, sections, day.iso, copy)
  units.push(...milk)
  for (const section of todayThenLater(sections)) {
    units.push(...mealUnits(sid, section, copy))
  }

  // Supply pull counts are for chefs; existing food container labels remain on both copies.
  const supplies =
    CHEF_SERVICE_MATERIALS_VISIBLE && copy === 'kitchen' ? visibleSupplyLines(campusSupplyRows([campus])) : []
  if (supplies.length > 0) {
    const group = `${sid}|supplies`
    units.push({ id: `${group}|band`, group, role: 'band', kind: 'band', text: 'SUPPLIES', note: null, level: 1 })
    for (const row of supplies) {
      units.push({
        id: `${group}|${JSON.stringify([row.skuId, row.isCompostable])}`,
        group,
        role: 'row',
        kind: 'shop-row',
        name: row.name,
        buy: row.qty.toLocaleString(),
        amount: null,
        usedFor: null,
        tag: row.isCompostable ? 'COMPOSTABLE' : null,
      })
    }
  }
  // Receiving is signed on the kitchen copy only, the one the driver brings back.
  // Both copies close with their Sign-Off box (the Oct 21 mockup's sign-off; the school copy only when the stop
  // is delivered: it has nothing else to sign). It closes the sheet's last table as its tail, so it never prints
  // alone on a page of its own (the mockup's "orphan sign-off pages"): a page break carries the last row over.
  if (copy === 'kitchen' || delivered) {
    const last = units[units.length - 1]
    units.push({
      id: `${sid}|signoff`,
      group: last?.group ?? null,
      role: last?.group ? 'tail' : 'block',
      kind: 'stop-signoff',
      dropOff: delivered,
      copy,
    })
  }

  return {
    id: sid,
    kind: 'stop-summary',
    dayIso: day.iso,
    // The Oct 21 canvas: the caterer on the kitchen copy, the one that leaves with the driver; a black SCHOOL COPY tag
    // on the school's. The stop's number and name are in the head below.
    band:
      copy === 'kitchen'
        ? {
            name: 'DELIVERY SHEET',
            counter: null,
            stopNumber: String(number),
            right: day.kitchenName.toUpperCase(),
            style: 'outline',
          }
        : {
            name: 'DELIVERY SHEET',
            counter: null,
            stopNumber: String(number),
            right: COPY_WORDS.school,
            style: 'solid',
            rightTag: true,
          },
    title: null,
    footer: { key: `${day.iso}|stops`, label: 'Stop Sheets', counted: false },
    units,
    repeat: [repeatOf(units[0]!)],
    preferOnePage: true,
    campusId: campus.id,
  }
}

/** Every stop's summary in route order: the kitchen copy when asked for, on top, then the school copy. */
export function stopSummarySections(day: PacketDay, kitchenCopy: boolean): PacketSection[] {
  return day.stops.flatMap((stop) => [
    ...(kitchenCopy ? [stopSummarySection(stop, day, 'kitchen')] : []),
    stopSummarySection(stop, day, 'school'),
  ])
}
